import {
    AfterViewInit,
    Component,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    QueryList,
    ViewChildren,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { filter, tap } from 'rxjs/operators';

import { NoteEntity } from '../coaching-base/config';
import { NoteCategory } from '../coaching-base/general';
import { BringIntoFocusService } from '../coaching-observation/bring-into-focus.service';
import {
    CmpsFormConnector,
    CmpsFormService,
    rollupErrors,
} from '../coaching-observation/experimental.form';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';

import { DefaultNoteInputs } from './default-note-input.model';
import { injectDefaultNotes } from './inject-default-notes.function';
import { NoteDetailFormValue } from './note-detail-form-value.model';
import { NoteDetailComponent } from './note-detail.component';
import { createNoteDetail } from './note-detail.form';
import { TranslationKeyMap } from './notes.i18n';

@Component({
    selector: '[cmpsNotes]',
    template: ` <ng-container
        *ngFor="let control of _form.controls; index as i"
    >
        <!--TODO revise form error styles-->
        <div
            class="w-100 my-2 alert-warning-compas"
            *ngIf="_form.dirty && control.errors != null"
        >
            <div
                class="h6 m-0 text-center alert-warning-compas"
                *ngIf="control.errors.required === true"
            >
                {{ _i18n._9800_THE_TEXT_IS_REQUIRED | translate }}
            </div>
        </div>
        <div
            cmpsNoteDetailNext
            [attr.data-trackingid]="_value[i].trackingId"
            [config]="config"
            [placeholder]="
                _defaultNoteInputs?.[_value[i].trackingId]?.placeholder
            "
            [hasTagBar]="hasTagBar"
            [hasCategoryBar]="hasCategoryBar"
            [required]="
                _defaultNoteInputs?.[_value[i].trackingId]?.required ?? false
            "
            [deletable]="
                _defaultNoteInputs?.[_value[i].trackingId]?.deletable ?? true
            "
            [cmpsFormControlPath]="cmpsFormControlPath?.concat(formControlName)"
            [formControl]="control"
            [showHeader]="showHeader"
            [index]="i"
        ></div>
    </ng-container>`,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NotesComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NotesComponent),
            multi: true,
        },
    ],
    exportAs: 'cmpsNotes',
})
@TakeUntilDestroy()
export class NotesComponent
    implements
        OnInit,
        AfterViewInit,
        OnDestroy,
        ControlValueAccessor,
        Validator,
        CmpsFormConnector
{
    @ViewChildren(NoteDetailComponent)
    readonly items: QueryList<NoteDetailComponent>;

    /** @private */ _i18n = TranslationKeyMap;
    @Input() config: NoteEntity;
    @Input() defaultCategory: NoteCategory;
    @Input() hasTagBar: boolean;
    @Input() hasCategoryBar: boolean;
    @Input() showHeader: boolean;
    @Input() cmpsFormControlPath: string[];
    @Input() formControlName: string;
    /** @private */ _form: FormArray;
    /** @private */ _defaultNoteInputs: DefaultNoteInputs;
    private _blackout: boolean;
    private _onChange: (_: NoteDetailFormValue[]) => void;
    private _onTouched: (_: any) => void;
    private _onValidatorChange: (_: any) => void;

    /** @private */ get _value(): NoteDetailFormValue[] {
        return this._form.value;
    }

    constructor(
        private readonly _formBuilder: FormBuilder,
        @Optional() private readonly _formService: CmpsFormService,
        @Optional() private readonly _focusService: BringIntoFocusService,
    ) {}

    ngOnInit(): void {
        this._form = new FormArray([]);
        if (this.cmpsFormControlPath != null && this.formControlName != null) {
            this._formService?.register(
                this.cmpsFormControlPath.concat(this.formControlName),
                this._form,
            );
        }

        this._form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                tap((value1) => {
                    console.log('_onChange notes');
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._onChange && this._onChange(value1);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    ngAfterViewInit(): void {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const run = () => {
            this.items.forEach((e, i) => {
                (e as any).onRemove(() => this.removeNote(i));
            });
        };
        this.items.changes
            .pipe(
                tap(() => {
                    run();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
        run();
    }

    ngOnDestroy(): void {
        if (this.cmpsFormControlPath != null && this.formControlName != null) {
            this._formService?.unregister(
                this.cmpsFormControlPath.concat(this.formControlName),
            );
        }
        this._form && (this._form as any).clear2();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled
            ? this._form.disable({ emitEvent: false })
            : this._form.enable({ emitEvent: false });
    }

    writeValue(obj: NoteDetailFormValue[]): void {
        this._patchForm(obj);
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        let errors = this._form.errors;
        const ret = rollupErrors(this._form);
        if (ret?._controls != null) {
            errors = { ...(errors ?? {}), ...ret._controls };
        }
        return errors;
    }

    addNote(note?: NoteDetailFormValue): void {
        const item = note ?? createNoteDetail(this.defaultCategory);
        this._form.push(this._formBuilder.control(item));

        if (this._focusService) {
            this._focusService.registerSelector(
                `[data-trackingid='${item.trackingId}']`,
                () => {
                    this.items.forEach((e) => e.blur());
                    this.items.last.focus();
                },
            );
        }
    }

    removeNote(index: number): void {
        this._form.removeAt(index);
    }

    focusLastNote(trackingId: string): void {
        const noteToFocus = this._value.find(
            (item) => item.trackingId.toString() === trackingId,
        );
        if (this._focusService && noteToFocus) {
            this._focusService.registerSelector(
                `[data-trackingid='${trackingId}']`,
                () => {
                    this.items.last.focus();
                },
            );
        }
    }

    private _patchForm(value: NoteDetailFormValue[]): void {
        this._blackout = true;

        (this._form as any).clear2();
        (this._form as any).push2(
            ...(value || []).map((e) => this._formBuilder.control(e)),
        );

        this._defaultNoteInputs = injectDefaultNotes(
            this._formBuilder,
            this._form,
            this.config?.defaultNotes,
            this.defaultCategory,
        );

        this._blackout = false;
    }
}
